/** Justvero made-to-order leather strap configurator. */
// Surcharge keys map to the add-on product's variants by SKU. No prices here:
// they live on the variants, reach the page through the addon map the block
// prints, and are read back with #surchargePrice. A price written twice is a
// price that eventually disagrees with itself.
// What each field says when it is missing. Written out rather than generated
// from the label: "Please select your lug width (mm)" carried the unit into the
// sentence, and "select" was wrong for the boxes you type into.
const FIELD_ERRORS = {
  lug_width_mm: 'Please select your lug width',
  buckle_width_mm: 'Please select your buckle width',
  buckle_width_mm_invalid: 'This buckle width is not made for your lug width and buckle style. Please choose one of the widths shown',
  length_code: 'Please select your strap length',
  custom_long_side_mm: 'Please enter the long side in millimetres',
  custom_short_side_mm: 'Please enter the short side in millimetres',
  stitch_color: 'Please choose a stitch colour',
  engraving_text: 'Please enter the text to engrave',
  watch_brand: 'Please enter your watch brand',
  watch_model: 'Please enter your watch model, or a short note',
};

const SURCHARGES = {
  SIGNATURE: { sku: 'JV-ADDON-PADDED', label: 'Signature style' },
  CURVED: { sku: 'JV-ADDON-CURVED', label: 'Curved end' },
  ENGRAVING: { sku: 'JV-ADDON-ENGRAVING', label: 'Engraving' },
  PRE_V: { sku: 'JV-ADDON-PRE-V', label: 'Pre-V buckle' },
  BUTTERFLY: { sku: 'JV-ADDON-BUTTERFLY', label: 'Butterfly clasp' },
  HIDDEN_DEPLOYANT: { sku: 'JV-ADDON-HIDDEN-DEPLOYANT', label: 'Hidden Deployant buckle' },
};

const FINISH_LABELS = { SILVER: 'Silver', GOLD: 'Gold', ROSE_GOLD: 'Rose Gold', BLACK: 'Black' };
const BUCKLE_LABELS = { CLASSIC: 'Classic', PRE_V: 'Pre-V', BUTTERFLY: 'Butterfly', HIDDEN_DEPLOYANT: 'Hidden Deployant' };
// The stored value stays SLIM — the metafield key and every existing order
// use it. Only the label the customer reads changed.
const STYLE_LABELS = { SIGNATURE: 'Signature', SLIM: 'Flat', STITCHLESS: 'Stitchless' };

const END_LABELS = { STRAIGHT: 'Straight', CURVED: 'Curved' };

const TAPER_LABELS = { straight: 'Straight', classic: 'Classic', slim: 'Slim' };

// Buckles only come in even sizes, so an odd lug tapers to an even buckle: 21
// can end at 20 but never at 21 or 19. Which even sizes exist depends on the
// buckle family — the workshop's list (Tee, 23/10). A width is valid when it is
// even, inside its family's range and no wider than the lug. "Other width",
// the taper cards, the buckle tiles and the add-to-cart check all read this.
// JV Master's khoaSai_ carries the same table for the supplier sheet.
const BUCKLE_RANGE = {
  CLASSIC: [10, 22],
  BUTTERFLY: [12, 22],
  PRE_V: [16, 24],
  HIDDEN_DEPLOYANT: [16, 22],
};

// The taper cards only: per lug, the widths offered as Straight / Classic /
// Slim, widest first. Follows the makers Tee checked against (Xilugs,
// Mocgoods); 13 and 15 are ours and step down the way 12 and 14 do. Each list
// is then narrowed to the buckle family, and a family that makes the lug's own
// width (Pre-V at 24) gets it as Straight.
const BUCKLE_BY_LUG = {
  12: [12, 10],
  13: [12, 10],
  14: [14, 12],
  15: [14, 12],
  16: [16, 14],
  17: [16, 14],
  18: [18, 16, 14],
  19: [18, 16],
  20: [20, 18, 16],
  21: [20, 18, 16],
  22: [22, 20, 18],
  23: [22, 20, 18],
  24: [22, 20],
};

const STAR_SVG = '<svg class="jv-cfg__taper-star" viewBox="0 0 10 10" aria-hidden="true" focusable="false"><path d="M5 0.4 6.16 3.5 9.4 3.68 6.9 5.77 7.77 8.9 5 7.15 2.23 8.9 3.1 5.77 0.6 3.68 3.84 3.5Z"/></svg>';
const STITCH_LABELS = {
  AS_PICTURED: 'As pictured', NO_STITCH: 'No stitch', AQUA_BLUE: 'Aqua Blue', BEIGE: 'Beige',
  BLACK: 'Black', BLUE: 'Blue', BLUE_GREY: 'Blue Grey', COGNAC: 'Cognac', DARK_BROWN: 'Dark Brown',
  DARK_GRAY: 'Dark Grey', DARK_NAVY: 'Dark Navy', GREEN: 'Green', LIGHT_BROWN: 'Light Brown',
  LIGHT_PINK: 'Light Pink', MINT: 'Mint', NAVY: 'Navy', OLIVE: 'Olive', ORANGE: 'Orange',
  PURPLE: 'Purple', RED: 'Red', TAUPE: 'Taupe', TURQUOISE: 'Turquoise', WHITE: 'White', YELLOW: 'Yellow',
};

const AXIS_SURCHARGE_KEYS = {
  style: { SIGNATURE: 'SIGNATURE' },
  buckle: { PRE_V: 'PRE_V', BUTTERFLY: 'BUTTERFLY', HIDDEN_DEPLOYANT: 'HIDDEN_DEPLOYANT' },
  watch_end: { CURVED: 'CURVED' },
};
const DEFAULT_META_MAP = {
  style: { slim: 'SLIM', padded: 'SIGNATURE', stitchless: 'STITCHLESS' },
  buckle: { classic: 'CLASSIC', 'pre-v': 'PRE_V', butterfly: 'BUTTERFLY', 'hidden-deployant': 'HIDDEN_DEPLOYANT' },
  watch_end: { straight: 'STRAIGHT', curved: 'CURVED' },
};

class LeatherStrapConfigurator extends HTMLElement {
  #addonMap = {};
  #basePrice = 0;
  #formId = '';
  #form = null;
  #initialVariantId = '';
  #configurationId = '';
  #formObserver = null;
  #dialogTriggers = new WeakMap();
  #galleryThumbBindings = [];
  #defaults = { style: null, buckle: null, watch_end: null };
  /** @type {string | null} The theme's own button wording, captured on connect. */
  #atcDefaultLabel = null;

  #stickyObserver = null;
  #stickyWanted = false;
  #resizeTimer = 0;
  #swatchesOpen = false;
  /** @type {null | {make: string, standard: string, priority: string, promoActive: boolean, deadline: null | {text: string, urgent: boolean}}} */
  #deliveryDates = null;

  connectedCallback() {
    this.#formId = this.dataset.formId || '';
    this.#initialVariantId = this.dataset.initialVariantId || '';
    this.#configurationId = globalThis.crypto?.randomUUID?.() || `jv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    this.#basePrice = (parseInt(this.dataset.basePriceCents || '0', 10) || 0) / 100;
    try {
      const map = this.querySelector('script[data-addon-map]');
      if (map) this.#addonMap = JSON.parse(map.textContent || '{}');
    } catch { /* fail closed for paid selections */ }

    this.addEventListener('change', this.#onChange);
    this.addEventListener('input', this.#onInput);
    this.addEventListener('click', this.#onClick);
    this.addEventListener('mousedown', this.#onFinishToggle);
    this.addEventListener('click', this.#onFinishToggle);
    for (const dialog of this.querySelectorAll('dialog[data-cfg-dialog]')) {
      dialog.addEventListener('click', event => {
        if (event.target === dialog) dialog.close();
      });
      dialog.addEventListener('close', () => {
        const trigger = this.#dialogTriggers.get(dialog);
        trigger?.focus();
        this.#dialogTriggers.delete(dialog);
      });
    }
    this.#parseDefaults();
    this.#bindProductFormSafetyGate();
    this.#bindProductGalleryThumbnails();
    this.#captureAtcLabel();
    this.#renderFit();
    this.#syncBuckleFamily();
    this.#syncWatchHelp();
    this.#syncConditionalControls();
    this.#syncHiddenProps();
    this.#observeAtc();
    this.#collapseSwatches();
    window.addEventListener('resize', this.#onResize);
    this.#updateSummary();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('input', this.#onInput);
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('mousedown', this.#onFinishToggle);
    this.removeEventListener('click', this.#onFinishToggle);
    this.#form?.removeEventListener('submit', this.#handleProductFormSubmit, true);
    window.removeEventListener('click', this.#handleAddToCartClick, true);
    document.removeEventListener('submit', this.#handleProductFormSubmit, true);
    this.#stickyObserver?.disconnect();
    this.#stickyObserver = null;
    window.removeEventListener('resize', this.#onResize);
    clearTimeout(this.#resizeTimer);
    window.removeEventListener('submit', this.#handleProductFormSubmit, true);
    this.#form?.removeEventListener('change', this.#handleExternalFormChange);
    this.#formObserver?.disconnect();
    for (const [thumbnail, handler] of this.#galleryThumbBindings) {
      thumbnail.removeEventListener('click', handler, true);
    }
    this.#galleryThumbBindings = [];
  }

  #bindProductGalleryThumbnails() {
    if (!customElements.get('slideshow-component')) {
      customElements.whenDefined('slideshow-component').then(() => {
        if (this.isConnected) this.#bindProductGalleryThumbnails();
      });
      return;
    }
    if (this.#galleryThumbBindings.length) return;

    const section = this.closest('.shopify-section') || document;
    const slideshow = section.querySelector('media-gallery slideshow-component');
    if (!slideshow || typeof slideshow.select !== 'function') return;

    const thumbnails = section.querySelectorAll(
      'media-gallery slideshow-controls.thumbnails .slideshow-controls__thumbnail'
    );
    thumbnails.forEach((thumbnail, index) => {
      const handler = event => {
        event.preventDefault();
        event.stopImmediatePropagation();
        slideshow.select(index, event, { animate: false });
      };
      thumbnail.addEventListener('click', handler, true);
      this.#galleryThumbBindings.push([thumbnail, handler]);
    });
  }

  #finishWasChecked = false;
  #onFinishToggle = event => {
    const el = event.target instanceof Element ? event.target : null;
    if (!el) return;
    const label = el.closest('label');
    const radio = el.dataset?.cfgField === 'buckle_finish' ? el
      : label?.querySelector('input[data-cfg-field="buckle_finish"]');
    if (!(radio instanceof HTMLInputElement)) return;
    const family = this.#radioVal('buckle_family') || 'CLASSIC';
    if (this.#defaults.buckle !== family) return;
    if (event.type === 'mousedown') { this.#finishWasChecked = radio.checked; return; }
    if (event.type === 'click' && this.#finishWasChecked) {
      radio.checked = false;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }
  };

  #onClick = event => {
    const button = event.target instanceof Element ? event.target.closest('button') : null;
    if (!button) return;

    if (button.dataset.swatchMore !== undefined) {
      this.#swatchesOpen = true;
      this.#collapseSwatches();
      return;
    }

    if (button.dataset.taperPick) {
      this.#showTaperNotice('');
      this.#setSelect('buckle_width_mm', parseInt(button.dataset.taperPick, 10));
      this.#toggleTaperOther(false);
      return;
    }

    if (button.dataset.taperOtherToggle !== undefined) {
      this.#toggleTaperOther(this.querySelector('[data-taper-other]')?.classList.contains('hidden'));
      return;
    }
    const dialogId = button.dataset.dialogOpen;
    if (dialogId) {
      const dialog = this.querySelector(`#${CSS.escape(dialogId)}`);
      if (dialog instanceof HTMLDialogElement) {
        this.#dialogTriggers.set(dialog, button);
        dialog.showModal();
      }
      return;
    }
    // The sticky bar does not add anything itself. It clicks the real button,
    // so #handleAddToCartClick, the field validation and the theme's own
    // submit all run exactly as they do from the main control.
    if (button.matches('[data-sticky-add]')) {
      this.#atcButton()?.click();
      return;
    }

    if (button.matches('[data-dialog-close]')) button.closest('dialog')?.close();

  };

  #onChange = event => {
    const element = event.target;

    // The engraving pills are a radio pair in front of the checkbox that
    // #getState, the hidden property and the surcharge all read. Mirror the
    // choice onto it and let its own change event do the rest.
    if (element instanceof HTMLInputElement && element.dataset.engravingChoice !== undefined) {
      const box = this.querySelector('input[data-cfg-field="engraving"]');
      if (box instanceof HTMLInputElement) {
        box.checked = element.value === 'ADD';
        box.dispatchEvent(new Event('change', { bubbles: true }));
      }
      return;
    }

    if (!(element instanceof HTMLInputElement || element instanceof HTMLSelectElement || element instanceof HTMLTextAreaElement)) return;
    const field = element.dataset.cfgField;

    // Clear field-level error when user interacts.
    if (field) this.#clearFieldError(field);

    // Picking a lug width also preselects the buckle, so a customer who stops
    // there still has a complete, sensible strap rather than a half-answered
    // question. #setSelect fires change, which re-enters here and repaints.
    if (field === 'lug_width_mm') {
      const lug = parseInt(element.value || '', 10);
      this.#showTaperNotice('');
      // A family that has no width for this lug (Hidden Deployant on a 14)
      // falls back to Classic, which fits every lug, and says so.
      const family = this.#family();
      if (Number.isFinite(lug) && !this.#familyFits(lug, family)) {
        this.#setRadio('buckle_family', 'CLASSIC');
        this.#showTaperNotice(`${BUCKLE_LABELS[family]} is made from ${BUCKLE_RANGE[family][0]}mm, so the buckle style was changed to Classic.`);
      }
      const preset = this.#defaultBuckle(lug);
      if (preset) this.#setSelect('buckle_width_mm', preset);
      this.#toggleTaperOther(false);
    }
    // Changing family can leave the chosen width outside the new range: move
    // to the nearest width that family makes, and say so rather than change it
    // silently.
    if (field === 'buckle_family') {
      const lug = parseInt(this.#selectVal('lug_width_mm') || '', 10);
      const buckle = parseInt(this.#selectVal('buckle_width_mm') || '', 10);
      if (Number.isFinite(lug) && Number.isFinite(buckle) && !this.#isBuckleAllowed(lug, buckle)) {
        const next = this.#nearestBuckle(lug, this.#family(), buckle);
        if (next) {
          this.#setSelect('buckle_width_mm', next);
          const [min, max] = BUCKLE_RANGE[this.#family()];
          this.#showTaperNotice(`Buckle width changed to ${next}mm — ${BUCKLE_LABELS[this.#family()]} is made from ${min} to ${max}mm.`);
        }
      }
    }
    // A width the customer picks clears the note; one the script sets keeps it.
    if (field === 'buckle_width_mm' && !this.#autoSetting) this.#showTaperNotice('');
    if (field === 'lug_width_mm' || field === 'buckle_width_mm' || field === 'buckle_family') this.#renderFit();

    if (field === 'length_code') {
      const custom = element.value === 'CUSTOM';
      const panel = this.querySelector('[data-conditional="length_custom"]');
      panel?.classList.toggle('hidden', !custom);
      for (const input of panel?.querySelectorAll('input') || []) {
        input.required = custom;
        if (!custom) input.value = '';
      }
    }
    if (field === 'style' || field === 'stitch_color_mode') this.#syncConditionalControls();
    if (field === 'buckle_family') this.#syncBuckleFamily();
    if (field === 'watch_help') this.#syncWatchHelp();
    if (element.matches('[data-watch-photo]')) {
      const fileName = this.querySelector('[data-file-name]');
      const file = element.files?.[0];
      if (fileName) {
        if (file && !/^image\/(jpeg|png|webp)$/.test(file.type)) {
          fileName.textContent = 'Please use JPEG, PNG, or WebP.';
          fileName.classList.add('has-file');
          element.value = '';
        } else if (file && file.size > 8 * 1024 * 1024) {
          fileName.textContent = 'Photo must be under 8 MB.';
          fileName.classList.add('has-file');
          element.value = '';
        } else {
          fileName.textContent = file?.name || 'No photo selected.';
          if (file) fileName.classList.add('has-file');
          else fileName.classList.remove('has-file');
        }
      }

      // Start shrinking as soon as the photo is chosen. Decoding and encoding
      // used to sit on the add-to-cart path, where the customer is waiting.
      // A later selection replaces this outright, so a discarded photo's work
      // is simply dropped.
      const accepted = element.files?.[0];
      this.#photoPrepared = accepted ? { file: accepted, promise: this.#resizePhoto(accepted) } : null;
    }
    if (field === 'engraving') {
      const panel = this.querySelector('[data-conditional="engraving"]');
      panel?.classList.toggle('hidden', !element.checked);
      const text = panel?.querySelector('[data-cfg-field="engraving_text"]');
      if (text instanceof HTMLInputElement) {
        text.required = element.checked;
        if (!element.checked) text.value = '';
        if (element.checked) text.focus();
      }
    }
    this.#syncHiddenProps();
    this.#updateSummary();
  };

  #onInput = event => {
    const element = event.target;
    if (!(element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement)) return;
    if (element.dataset.cfgField === 'engraving_text') {
      const count = this.querySelector('[data-engrave-count]');
      if (count) count.textContent = `${element.value.length} / 12`;
    }
    this.#syncHiddenProps();
    this.#updateSummary();
  };

  /**
   * Brings the watch-details panel in line with its toggle.
   *
   * Runs at connect as well as on change, because the toggle's starting state
   * is a block setting now rather than always on. Reading it from the checkbox
   * rather than assuming keeps the two in step whichever way the setting is
   * left — the block renders the panel hidden and its inputs disabled to match,
   * so nothing flashes before this runs.
   */
  /**
   * Finds an element that belongs to this configurator but may be rendered
   * outside it.
   *
   * The build summary is its own block now, placed below the checkout badges,
   * so it is a sibling rather than a descendant. The search is bounded to the
   * enclosing section: two configurators on one page would otherwise write
   * into each other's summary, and a document-wide query would make that
   * silent. Nothing found means nothing to fill — the block is optional.
   */
  #scopeRoot() {
    // Not `[data-section-id]`: this element carries that attribute itself, and
    // closest() starts at the element, so the scope collapsed to the
    // configurator and the build summary — a sibling block — was never found.
    // The failure was silent by design, which is how it reached live.
    return this.closest('.shopify-section') ?? this.parentElement?.closest('[data-section-id]') ?? this;
  }

  #inSection(selector) {
    return this.querySelector(selector) ?? this.#scopeRoot().querySelector(selector);
  }

  #allInSection(selector) {
    return this.#scopeRoot().querySelectorAll(selector);
  }

  #family() {
    const family = this.#radioVal('buckle_family');
    return BUCKLE_RANGE[family] ? family : 'CLASSIC';
  }

  /** The widths the taper cards offer for this lug and family, widest first. */
  #allowedBuckles(lug, family = this.#family()) {
    const pool = [...(BUCKLE_BY_LUG[lug] || [])];
    const top = this.#evenFloor(lug);
    if (pool.length && top > pool[0]) pool.unshift(top);
    return pool.filter((b) => this.#isBuckleAllowed(lug, b, family));
  }

  /** Even, inside the family's range, and no wider than the lug. */
  #isBuckleAllowed(lug, buckle, family = this.#family()) {
    const l = parseInt(lug, 10);
    const b = parseInt(buckle, 10);
    const range = BUCKLE_RANGE[family];
    if (!range || !Number.isFinite(l) || !Number.isFinite(b)) return false;
    return b % 2 === 0 && b >= range[0] && b <= Math.min(l, range[1]);
  }

  /** Whether the family makes any width at all for this lug. */
  #familyFits(lug, family) {
    const range = BUCKLE_RANGE[family];
    return Boolean(range) && range[0] <= this.#evenFloor(Math.min(lug, range[1]));
  }

  /** The valid width closest to `target`, the narrower one on a tie. */
  #nearestBuckle(lug, family, target) {
    let best = null;
    for (let b = 0; b <= 30; b += 2) {
      if (!this.#isBuckleAllowed(lug, b, family)) continue;
      if (best === null || Math.abs(b - target) < Math.abs(best - target)) best = b;
    }
    return best;
  }

  /**
   * The buckle a lug width preselects: the default taper measured from the
   * widest even width that fits, so 21 and 20 both land on 18 — or the
   * nearest width the chosen family makes.
   */
  #defaultBuckle(lug) {
    if (!Number.isFinite(lug)) return null;
    return this.#nearestBuckle(lug, this.#family(), this.#evenFloor(lug) - this.#taperOffset());
  }

  /** Checks a radio the markup owns and lets the normal change handling run. */
  #setRadio(field, value) {
    const radio = this.querySelector(`input[data-cfg-field="${field}"][value="${value}"]`);
    if (!(radio instanceof HTMLInputElement) || radio.checked) return;
    radio.checked = true;
    radio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  /** The line under the taper cards that says what the configurator changed by itself. */
  #showTaperNotice(text) {
    const el = this.querySelector('[data-taper-notice]');
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('hidden', !text);
  }

  #evenFloor(n) {
    return n % 2 === 0 ? n : n - 1;
  }

  /** How much narrower the buckle end is by default, from the block setting. */
  #taperOffset() {
    const raw = parseInt(this.dataset.taperDefaultOffset || '2', 10);
    return Number.isFinite(raw) ? raw : 2;
  }

  /**
   * Names a taper by how much it narrows, which is the thing a customer can
   * see. Straight is not a taper at all; past 2mm the shape reads differently
   * enough to deserve its own name.
   */
  #taperKey(lug, buckle) {
    // Measured from the widest even buckle, not from the lug: on a 21 lug the
    // straightest strap there is ends at 20, and that one is Straight.
    const d = this.#evenFloor(lug) - buckle;
    if (d <= 0) return 'straight';
    if (d <= 2) return 'classic';
    return 'slim';
  }

  #taperName(lug, buckle) {
    return TAPER_LABELS[this.#taperKey(lug, buckle)];
  }

  /**
   * The shortcuts offered for a lug width: every buckle width the table makes
   * for it, which is at most three — straight, the default taper, and one step
   * narrower. A width the table leaves out gets no card, so 24 offers Classic
   * and Slim and no Straight.
   */
  #taperOptions(lug) {
    const preset = this.#defaultBuckle(lug);
    // Cards follow the family too: a 24 lug offers 24 only with Pre-V.
    return this.#allowedBuckles(lug)
      .map((buckle) => ({ buckle, name: this.#taperName(lug, buckle), isDefault: buckle === preset }));
  }

  /** Writes a value into a select the markup still owns, and lets the rest of
   *  the component react through its normal change handling. */
  #setSelect(field, value) {
    const select = this.querySelector(`select[data-cfg-field="${field}"]`);
    if (!(select instanceof HTMLSelectElement)) return;
    select.value = String(value);
    const outer = this.#autoSetting;
    this.#autoSetting = true;
    try { select.dispatchEvent(new Event('change', { bubbles: true })); } finally { this.#autoSetting = outer; }
  }

  /** True while #setSelect's own change event runs, so handlers can tell it from a customer's. */
  #autoSetting = false;

  /** Shows or hides the "Other width" select and keeps the link's state with it. */
  #toggleTaperOther(open) {
    this.querySelector('[data-taper-other]')?.classList.toggle('hidden', !open);
    this.querySelector('[data-taper-other-toggle]')?.setAttribute('aria-expanded', String(Boolean(open)));
  }

  /**
   * Paints the taper buttons.
   *
   * Text only: at a believable scale the three shapes differ by a couple of
   * pixels, and the millimetres on the button say the same thing without
   * pretending to be a picture of the strap.
   */
  #renderFit() {
    const lug = parseInt(this.#selectVal('lug_width_mm') || '', 10);
    const buckle = parseInt(this.#selectVal('buckle_width_mm') || '', 10);
    const hasLug = Number.isFinite(lug);

    const field = this.querySelector('[data-taper-field]');
    field?.classList.toggle('hidden', !hasLug);
    if (!hasLug) return;

    const cards = this.querySelector('[data-taper-cards]');
    if (cards) {
      cards.innerHTML = this.#taperOptions(lug)
        .map(({ buckle: b, name, isDefault }) => [
          `<button type="button" class="jv-cfg__taper-card${b === buckle ? ' is-selected' : ''}" data-taper-pick="${b}" aria-pressed="${b === buckle}">`,
          // The star marks the option, so it rides with the name. On the
          // millimetre line it cost 14px the card does not have at 360px, and
          // "14mm → 12mm" wrapped on the one card that carries it.
          // Drawn rather than typed: the character renders as a colour glyph
          // on iOS and as a missing box in a few Windows fonts.
          `<span class="jv-cfg__taper-card-name">${this.#escapeHtml(name)}`,
          isDefault ? STAR_SVG + '<span class="visually-hidden">Most popular</span>' : '',
          '</span>',
          `<span class="jv-cfg__taper-card-mm">${lug}mm&thinsp;&rarr;&thinsp;${b}mm</span>`,
          // An odd lug has no truly straight strap; say so under the card
          // rather than let "Straight" promise 21 → 21.
          name === TAPER_LABELS.straight && b !== lug ? '<span class="jv-cfg__taper-card-note">near-straight</span>' : '',
          '</button>',
        ].join(''))
        .join('');
    }

    // "Other width" lists every even width this lug can take, wider than the
    // cards. Hidden as well as disabled: iOS shows disabled options, and a
    // greyed-out width reads as "sold out" rather than "does not exist".
    for (const option of this.querySelectorAll('select[data-cfg-field="buckle_width_mm"] option')) {
      const value = parseInt(option.value, 10);
      if (!Number.isFinite(value)) continue;
      const ok = this.#isBuckleAllowed(lug, value);
      option.disabled = !ok;
      option.hidden = !ok;
    }

    // A family with no width for this lug is shown but cannot be picked, with
    // the reason under its name. Its own <small> belongs to
    // #updateOptionLabels, so the reason gets an element of its own.
    for (const radio of this.querySelectorAll('input[data-cfg-field="buckle_family"]')) {
      const fits = this.#familyFits(lug, radio.value);
      radio.disabled = !fits;
      const face = radio.closest('label')?.querySelector('.jv-cfg__pill-face');
      let note = face?.querySelector('[data-family-note]');
      if (!fits && face && !note) {
        note = document.createElement('em');
        note.className = 'jv-cfg__pill-note';
        note.dataset.familyNote = '';
        face.append(note);
      }
      if (note) note.textContent = fits ? '' : `From ${BUCKLE_RANGE[radio.value]?.[0]}mm`;
    }
  }

  /**
   * The value line beside a group label. Built from the same #isDefault /
   * #isIncluded / #getAxisOptionPrice the total is built from, so a row can
   * never advertise a price the cart does not charge.
   */
  #rowValue(axis, key, label) {
    const price = this.#getAxisOptionPrice(axis, key);
    if (price > 0 && !this.#isIncluded(axis, key)) {
      return `${this.#escapeHtml(label)} <span class="jv-cfg__surcharge">${this.#formatSurcharge(price)}</span>`;
    }
    // The product's own default reads "Butterfly — as pictured · included":
    // the dash ties "as pictured" to the option it describes, and the middot
    // keeps "included" as the same aside it is on every other row.
    const note = this.#isDefault(axis, key) ? '&mdash; as pictured &middot; included' : '&middot; included';
    return `${this.#escapeHtml(label)} <span class="jv-cfg__row-note">${note}</span>`;
  }

  /**
   * The buckle row's value line. Its own builder rather than #rowValue
   * because the buckle is the axis where "included" says too little: a strap
   * sold with a butterfly clasp includes three of the four families, and only
   * one of the three is the one in the photographs.
   *
   * Three outcomes, and they are the tile's three, lower-cased — the row is
   * an aside, the tile is a label. The surcharge branch is the exact inverse
   * of the free one, both read off #getAxisOptionPrice, so the row cannot
   * quote a figure the cart does not charge.
   */
  #buckleRowValue(state) {
    const key = state.buckle_type;
    const defaultKey = this.#effectiveBuckleDefault();

    // The finish is dropped from the buckle a strap is assumed to be
    // photographed with but never declares: there the Silver radio is checked
    // by the block's markup and not by the customer, and "Classic Silver · as
    // pictured" would assert a finish no product record claims. Every other
    // family on such a strap still shows its finish — it is stating the
    // configuration, not what the photographs show.
    const showFinish = Boolean(this.#defaults.buckle) || key !== defaultKey;
    const finish = !showFinish || state.buckle_finish === 'AS_PICTURED'
      ? ''
      : (FINISH_LABELS[state.buckle_finish] || '');
    const label = this.#escapeHtml(`${BUCKLE_LABELS[key] || key} ${finish}`.trim());
    const price = this.#getAxisOptionPrice('buckle', key);

    if (key !== defaultKey && price > this.#getAxisOptionPrice('buckle', defaultKey)) {
      return `${label} <span class="jv-cfg__row-note">&middot;</span> <span class="jv-cfg__surcharge">${this.#formatSurcharge(price)}</span>`;
    }
    const note = key === defaultKey ? this.#buckleLabelDefault() : this.#buckleLabelFree();
    return `${label} <span class="jv-cfg__row-note">&middot; ${this.#escapeHtml(note.toLowerCase())}</span>`;
  }

  #renderRowValues(state) {
    const values = {
      watch_end: this.#rowValue('watch_end', state.watch_end_shape, END_LABELS[state.watch_end_shape] || state.watch_end_shape),
      style: this.#rowValue('style', state.style, STYLE_LABELS[state.style] || state.style),
      buckle: this.#buckleRowValue(state),
      // Stitching never carries a surcharge, so a note would only add noise.
      stitch: this.#escapeHtml(STITCH_LABELS[state.stitch_color] || 'Select a colour'),
      engraving: state.engraving === 'true'
        ? `${state.engraving_text ? `&ldquo;${this.#escapeHtml(state.engraving_text)}&rdquo;` : 'Added'} <span class="jv-cfg__surcharge">${this.#formatSurcharge(this.#surchargePrice('ENGRAVING'))}</span>`
        : 'None',
    };

    for (const [key, html] of Object.entries(values)) {
      const target = this.querySelector(`[data-row-value="${key}"]`);
      if (target) target.innerHTML = html;
    }
  }

  /* ─── Delivery estimate ─── */

  /**
   * Today's calendar date in a named zone, as a UTC midnight — never the
   * browser's own date. A customer in Sydney must not see the estimate jump a
   * day because their clock is ahead of the workshop's.
   *
   * Two zones are in play and they are different on purpose: the dates are
   * the workshop's, in Los Angeles, and the promotion's deadline is the
   * store's trading deadline, in New York. The three hours between them are
   * the three hours in which a code still works but the workshop has not
   * started tomorrow yet.
   */
  #todayIn(timeZone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone, year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(new Date()).split('-').map(Number);
    return Date.UTC(parts[0], parts[1] - 1, parts[2]);
  }

  /**
   * Business days are Monday to Saturday — Sunday the only day off — and the
   * same count applies to production and to transit, because that is how the
   * Google Merchant Center shipping settings are configured and the two must
   * not drift apart. The owner can turn Saturday off in the editor if GMC
   * changes.
   */
  #addBusinessDays(ms, count, includeSaturday) {
    let time = ms;
    // Capped: a mistyped number in the editor should not spin the main thread.
    let left = Math.min(Math.max(0, count), 365);
    while (left > 0) {
      time += 86400000;
      const day = new Date(time).getUTCDay();
      if (day === 0) continue;
      if (day === 6 && !includeSaturday) continue;
      left -= 1;
    }
    return time;
  }

  #formatDay(ms) {
    return new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' }).format(new Date(ms));
  }

  #formatRange(from, to) {
    return from === to ? this.#formatDay(from) : `${this.#formatDay(from)} \u2013 ${this.#formatDay(to)}`;
  }

  #deliveryNumber(name, fallback) {
    const raw = parseInt(this.dataset[name] || '', 10);
    return Number.isFinite(raw) && raw >= 0 ? raw : fallback;
  }

  /**
   * A YYYY-MM-DD day, or null for anything that is not exactly that — a
   * half-typed or cleared promotion date must read as "no promotion", never as
   * a promotion with no end or a thrown error that takes the rest of the
   * estimate down with it.
   */
  #parseIsoDay(raw) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec((raw || '').trim());
    if (!match) return null;
    const year = Number(match[1]);
    const month = Number(match[2]);
    const day = Number(match[3]);
    const ms = Date.UTC(year, month - 1, day);
    const back = new Date(ms);
    if (back.getUTCFullYear() !== year || back.getUTCMonth() !== month - 1 || back.getUTCDate() !== day) return null;
    return ms;
  }

  /** The threshold is a round number in the editor, so it reads as one here. */
  #formatThreshold(amount) {
    return Number.isInteger(amount) ? `$${amount}` : `$${amount.toFixed(2)}`;
  }

  /**
   * Everything in the estimate that the customer cannot change: the dates, and
   * whether the promotion is still running. Computed once per page — the
   * alternative is six Intl formatters and a date walk on every keystroke in
   * the engraving box.
   */
  #computeDeliveryDates() {
    const saturday = this.dataset.saturdayBusiness !== 'false';
    const pMin = this.#deliveryNumber('productionMin', 5);
    const pMax = Math.max(pMin, this.#deliveryNumber('productionMax', 7));
    const today = this.#todayIn('America/Los_Angeles');
    const shipFrom = this.#addBusinessDays(today, pMin, saturday);
    const shipTo = this.#addBusinessDays(today, pMax, saturday);

    // The window opens at the earliest ship date and closes at the latest, so
    // the two ends come from different ship dates rather than the same one.
    const arrive = (min, max) => this.#formatRange(
      this.#addBusinessDays(shipFrom, min, saturday),
      this.#addBusinessDays(shipTo, Math.max(min, max), saturday)
    );

    const promoEnd = this.#parseIsoDay(this.dataset.promoEnd);
    const deadline = this.#promoDeadline(promoEnd);
    return {
      make: (this.dataset.productionText || 'Handmade to order \u00b7 ships in {pmin}\u2013{pmax} business days')
        .replace('{pmin}', String(pMin))
        .replace('{pmax}', String(pMax)),
      standard: arrive(this.#deliveryNumber('standardMin', 10), this.#deliveryNumber('standardMax', 15)),
      priority: arrive(this.#deliveryNumber('priorityMin', 7), this.#deliveryNumber('priorityMax', 10)),
      promoActive: this.dataset.promoEnabled !== 'false' && deadline !== null,
      deadline,
    };
  }

  /**
   * How the end of the promotion is worded, and whether it is worth shouting
   * about. The count is in whole calendar days in New York, so a code that
   * works until the end of the 30th says "Last day" for the whole of the
   * 30th there and nothing at all on the 1st — wherever the customer is.
   *
   * null means the promotion is over, which is also what an unreadable date
   * gives: a half-typed setting should read as no promotion, not as one
   * running forever.
   */
  #promoDeadline(promoEnd) {
    if (promoEnd === null) return null;
    const days = Math.round((promoEnd - this.#todayIn('America/New_York')) / 86400000);
    if (days < 0) return null;
    if (days === 0) return { text: this.dataset.promoLast || 'Last day', urgent: true };
    if (days === 1) return { text: this.dataset.promoTomorrow || 'Ends tomorrow', urgent: true };
    if (days <= 7) {
      return { text: (this.dataset.promoSoon || 'Ends in {n} days').replace('{n}', String(days)), urgent: true };
    }
    return { text: (this.dataset.promoFar || 'Ends {date}').replace('{date}', this.#formatDay(promoEnd)), urgent: false };
  }

  /** Owner-entered text, so it goes in as text — never as markup. */
  #deliveryPrice(text) {
    const node = document.createElement('strong');
    node.className = 'jv-cfg__delivery-price';
    node.textContent = text;
    return node;
  }

  /**
   * Written by the script rather than by Liquid: the section markup is cached,
   * so a date rendered server-side would go stale without anything changing
   * to invalidate it. Called from #updateSummary with the estimated total,
   * because the Standard price turns into "Free" as that total crosses the
   * threshold — the one part of the estimate the customer moves.
   */
  #renderDelivery(total = 0) {
    const box = this.querySelector('[data-delivery]');
    if (!box) return;

    this.#deliveryDates ||= this.#computeDeliveryDates();
    const dates = this.#deliveryDates;

    const make = box.querySelector('[data-delivery-make]');
    if (make) make.textContent = dates.make;

    const setText = (selector, text) => {
      const node = box.querySelector(selector);
      if (node) node.textContent = text;
    };
    setText('[data-ship-date="standard"]', dates.standard);
    setText('[data-ship-date="priority"]', dates.priority);
    setText('[data-ship-price="priority"]', this.dataset.priorityPrice || '$12.99');

    const standardPrice = this.dataset.standardPrice || '$7.99';
    const threshold = this.#deliveryNumber('freeThreshold', 150);
    // The threshold outranks the promotion. An order already over it ships
    // free with nothing typed, and showing that customer a discount code
    // would be asking them to do work for something they already have.
    const freeOnTotal = total >= threshold;
    const promoRunning = !freeOnTotal && dates.promoActive;

    const price = box.querySelector('[data-ship-price="standard"]');
    if (price) {
      price.textContent = '';
      if (promoRunning) {
        const was = document.createElement('s');
        was.textContent = standardPrice;
        price.append(was);
      }
      price.append(this.#deliveryPrice(freeOnTotal || promoRunning ? 'Free' : standardPrice));
    }

    // Under the table, and only one of the two: the tag when a code is the
    // thing standing between this order and free shipping, the note when
    // spending more is.
    const promo = box.querySelector('[data-ship-promo]');
    if (promo) {
      promo.hidden = !promoRunning;
      // Only the deadline is written. The code and the words around it were
      // rendered by Liquid and do not change, so there is nothing to replace
      // and nothing to reflow — the tag is the same shape before and after.
      const deadline = promo.querySelector('[data-ship-deadline]');
      if (promoRunning && deadline && dates.deadline) {
        deadline.textContent = dates.deadline.text;
        deadline.classList.toggle('jv-cfg__ship-deadline--urgent', dates.deadline.urgent);
      }
    }

    const note = box.querySelector('[data-ship-note]');
    if (note) {
      const show = !freeOnTotal && !promoRunning;
      note.hidden = !show;
      if (show) note.textContent = `Free standard shipping over ${this.#formatThreshold(threshold)}`;
    }
  }

  /* ─── Colour swatches: a few rows on a phone ─── */

  #onResize = () => {
    clearTimeout(this.#resizeTimer);
    this.#resizeTimer = setTimeout(() => this.#collapseSwatches(), 150);
  };

  /**
   * Sixteen colours is four rows of swatches above the fit questions, which
   * on a phone is most of a screen before the first thing to answer. Three
   * rows and a "+N" tile instead, opening in place. Desktop has the same
   * problem in smaller print, and gets its own row count.
   *
   * How many fit on a row is a layout fact, not a number to assume: the items
   * are grouped by the top they land on. Reordering is done with flex `order`
   * rather than by moving nodes, so the current colour can be pulled into the
   * visible rows without touching the DOM the snippet rendered.
   */
  #collapseSwatches() {
    const row = this.querySelector('.jv-colorway__row');
    if (!row) return;

    // The tile wears .jv-colorway__swatch so it matches the colours beside it.
    // Without the :not() it counted itself on every run after the first, and
    // "+2" became "+3" on the first resize.
    const swatches = [...row.querySelectorAll('.jv-colorway__swatch:not(.jv-colorway__more)')];
    const more = row.querySelector('[data-swatch-more]');
    const reset = () => {
      for (const s of swatches) { s.classList.remove('is-hidden'); s.style.order = ''; }
      more?.remove();
    };

    // An opened row shows every colour.
    if (this.#swatchesOpen) { reset(); return; }

    const rows = window.matchMedia('(max-width: 749px)').matches
      ? this.#deliveryNumber('swatchRows', 3)
      : this.#deliveryNumber('swatchRowsDesktop', 2);
    reset();
    // Nought rows is the editor's way of asking for the whole set.
    if (rows < 1 || !swatches.length) return;

    const firstTop = Math.round(swatches[0].getBoundingClientRect().top);
    const perRow = swatches.filter((s) => Math.round(s.getBoundingClientRect().top) === firstTop).length;
    if (!perRow || swatches.length <= rows * perRow) return;

    // One slot of the last row belongs to the tile, and when the current
    // colour has to be carried in from further down it takes a slot too —
    // otherwise the grid runs to an extra row holding nothing but the tile.
    const limit = rows * perRow - 1;
    const currentIndex = swatches.findIndex((s) => s.classList.contains('jv-colorway__swatch--current'));
    const carryCurrent = currentIndex >= limit;
    const lead = carryCurrent ? limit - 1 : limit;

    swatches.forEach((swatch, index) => {
      const keep = index < lead || index === currentIndex;
      swatch.classList.toggle('is-hidden', !keep);
      // The current colour, when it lives past the cut, sits last of the ones
      // on show — in the rows, and next to the tile that opens the rest.
      if (keep && index >= lead) swatch.style.order = '1';
    });

    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'jv-colorway__swatch jv-colorway__more';
    button.dataset.swatchMore = '';
    button.style.order = '2';
    const hidden = swatches.length - limit;
    button.textContent = `+${hidden}`;
    button.setAttribute('aria-label', `Show ${hidden} more colours`);
    row.append(button);
  }

  /* ─── Mobile sticky add to cart ─── */

  #atcButton() {
    const form = document.getElementById(this.#formId);
    const button = form?.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    return button instanceof HTMLButtonElement ? button : null;
  }

  /**
   * Watches the real add to cart button: the bar stands in for it whenever it
   * is not on screen, in either direction. It used to require
   * `boundingClientRect.top < 0`, so it only appeared after the customer had
   * scrolled past the button — which is every moment except the one where it
   * is least needed. While they are choosing a colour, a lug width or an
   * option the button is below the fold, and that is when a running total and
   * a way to buy are worth having.
   *
   * The root is shortened by the bar's own height so "on screen" means on
   * screen rather than behind the bar: tied to the button this way, the bar
   * can never cover the control it stands in for.
   */
  #observeAtc() {
    const button = this.#atcButton();
    const bar = this.querySelector('[data-sticky-bar]');
    if (!button || !bar || typeof IntersectionObserver !== 'function') return;

    this.#stickyObserver = new IntersectionObserver(
      ([entry]) => {
        // Half the button showing is enough to hand back to it.
        this.#stickyWanted = entry.intersectionRatio < 0.5;
        this.#syncStickyBar();
      },
      { threshold: [0, 0.5], rootMargin: `0px 0px -${this.#stickyHeight()}px 0px` }
    );
    this.#stickyObserver.observe(button);
  }

  /**
   * The bar's height while it is hidden, which is when this is needed.
   * Padding is a computed value and survives display:none, so it can be read
   * without showing the bar — and it carries env(safe-area-inset-bottom),
   * which is most of the difference between one phone and another.
   */
  #stickyHeight() {
    const bar = this.querySelector('[data-sticky-bar]');
    if (!bar) return 64;
    const cs = getComputedStyle(bar);
    const padding = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    return Math.round(padding + 48 + 1);
  }

  /**
   * The drawer, the guide dialogs and the chat widget all paint above the bar
   * without being asked to: a modal <dialog> is in the top layer, which no
   * z-index can reach, and the widgets sit far above --layer-sticky. So there
   * is nothing to coordinate here — the bar only tracks the button.
   */
  #syncStickyBar() {
    const bar = this.querySelector('[data-sticky-bar]');
    if (!bar) return;
    bar.hidden = !this.#stickyWanted;
    bar.classList.toggle('is-visible', this.#stickyWanted);
  }

  #renderStickyBar(total) {
    const totalTarget = this.querySelector('[data-sticky-total]');
    if (totalTarget) totalTarget.textContent = this.#formatMoney(total);

    // The bar never states anything the main button does not: its label is
    // read back from that button after #setAtcIdleLabel has written it.
    const label = this.querySelector('[data-sticky-label]');
    const source = this.#atcTextTarget();
    if (label && source) label.textContent = source.textContent?.trim() || 'Add to cart';

    const button = this.querySelector('[data-sticky-add]');
    const real = this.#atcButton();
    if (button && real) button.disabled = real.disabled;
  }

  #syncWatchHelp() {
    const toggle = this.querySelector('[data-cfg-field="watch_help"]');
    if (!(toggle instanceof HTMLInputElement)) return;

    const enabled = toggle.checked;
    const panel = this.querySelector('[data-conditional="watch_help"]');
    panel?.classList.toggle('hidden', !enabled);

    for (const control of panel?.querySelectorAll('input') || []) {
      control.disabled = !enabled;
      const needsWatchText = control.matches('[data-cfg-field="watch_brand"], [data-cfg-field="watch_model"]');
      control.required = enabled && needsWatchText;
      if (!enabled) control.value = '';
    }

    if (!enabled) {
      // Clearing the file input already stops the photo being sent. Drop the
      // prepared copy too: it holds the original and a resized blob, and after
      // switching the toggle off neither is going anywhere.
      this.#photoPrepared = null;
    }

    const fileName = this.querySelector('[data-file-name]');
    if (fileName) {
      fileName.textContent = '';
      fileName.classList.remove('has-file');
    }
  }

  #syncConditionalControls() {
    const style = this.#radioVal('style') || 'SLIM';
    const mode = this.#radioVal('stitch_color_mode') || 'AS_PICTURED';
    const modeRadios = this.querySelectorAll('input[data-cfg-field="stitch_color_mode"]');
    const customPanel = this.querySelector('[data-conditional="stitch_custom"]');
    const customRadios = customPanel?.querySelectorAll('input[type="radio"]') || [];
    const stitchless = style === 'STITCHLESS';
    modeRadios.forEach(radio => { radio.disabled = stitchless; });
    this.querySelector('[data-no-stitch]')?.classList.toggle('hidden', !stitchless);
    const showCustom = !stitchless && mode === 'CUSTOM';
    customPanel?.classList.toggle('hidden', !showCustom);
    customRadios.forEach((radio, index) => {
      radio.disabled = !showCustom;
      radio.required = showCustom && index === 0;
      if (stitchless) radio.checked = false;
    });
  }

  #syncBuckleFamily() {
    const family = this.#radioVal('buckle_family') || 'CLASSIC';
    const isDefaultFamily = this.#defaults.buckle === family;
    for (const panel of this.querySelectorAll('[data-buckle-panel]')) {
      const active = panel.dataset.bucklePanel === family;
      panel.classList.toggle('hidden', !active);
      const finishes = panel.querySelectorAll('input[data-cfg-field="buckle_finish"]');
      finishes.forEach(input => { input.disabled = !active; });
      if (active) {
        if (isDefaultFamily) {
          this.#syncDefaultBuckleFinish();
        } else if (![...finishes].some(input => input.checked)) {
          finishes[0]?.click();
        }
      }
    }
  }

  #bindProductFormSafetyGate() {
    this.#form = document.getElementById(this.#formId);
    if (!(this.#form instanceof HTMLFormElement)) return;
    this.#form.addEventListener('submit', this.#handleProductFormSubmit, true);
    document.addEventListener('submit', this.#handleProductFormSubmit, true);
    window.addEventListener('submit', this.#handleProductFormSubmit, true);
    // The theme animates a thumbnail flying to the cart icon from
    // ProductFormComponent#handleClick, wired by `on:click="/handleClick"` and
    // dispatched by the component framework through
    // `document.addEventListener('click', …, { capture: true })`. It fires on
    // the click, before any submit, so an incomplete build used to get the
    // animation and then the refusal.
    //
    // Window, in capture, because the capture path runs window → document:
    // registering on document would queue behind the framework's listener,
    // which is installed when the theme's scripts load, long before this block.
    window.addEventListener('click', this.#handleAddToCartClick, true);
    this.#form.addEventListener('change', this.#handleExternalFormChange);

    const component = this.#form.closest('product-form-component');
    if (!component) return;
    // The observer used to re-disable the button whenever the theme enabled it
    // while the build was incomplete. That is the behaviour being removed, and
    // the submit handler — bound on the form, the document and the window, all
    // in capture — is what actually refuses an incomplete add.
    this.#formObserver = new MutationObserver(() => {
      const { unresolved } = this.#calcTotal(this.#getState());
      if (unresolved.length === 0) return;
      const button = component.querySelector('[ref="addToCartButton"]');
      if (button instanceof HTMLButtonElement && !button.disabled) this.#setBusyState(true);
    });
    this.#formObserver.observe(component, { subtree: true, attributes: true, attributeFilter: ['disabled', 'value'] });
  }

  #handleExternalFormChange = () => queueMicrotask(() => {
    this.#syncHiddenProps();
    this.#updateSummary();
  });

  /**
   * Stops a click on add to cart before the theme reacts to it, when the build
   * is not finished.
   *
   * Nothing happens here when it is: the theme animates, submits, and
   * #handleProductFormSubmit takes over as before. This only exists so that an
   * incomplete build gets the validation and nothing else — a thumbnail flying
   * to the cart says the opposite of what is about to happen.
   */
  #handleAddToCartClick = event => {
    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('[ref="addToCartButton"]');
    if (!button) return;

    // Only this product's button: a page can hold a quick-add form too.
    //
    // Compared by identity, not by id. HTMLFormElement exposes its named
    // controls as properties, and the product form carries
    // <input type="hidden" name="id"> for the variant — so `form.id` is that
    // input element, not the string. The comparison could never be true and
    // this gate returned early on every click, which is why the animation
    // still played. #handleProductFormSubmit was unaffected because it already
    // compared elements.
    if (button.closest('form') !== this.#form) return;

    if (!this.#shouldBlockCheckout()) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();
    this.#syncHiddenProps();
    this.#updateSummary();
    this.#showValidation();
  };

  #handleProductFormSubmit = event => {
    if (event.target !== this.#form) return;
    this.#syncHiddenProps();
    this.#updateSummary();
    if (this.#shouldBlockCheckout()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.#showValidation();
      return;
    }

    // Clear any lingering errors before submitting.
    this.#clearAllFieldErrors();

    // Always intercept: we handle the cart add ourselves for atomic multi-item.
    event.preventDefault();
    event.stopImmediatePropagation();
    this.#atomicCartAdd();
  };

  /** Atomic multi-item cart add: parent strap + add-on variants in one request.
   *  Properties mirror the hidden inputs exactly (contract §3.1). */
  async #atomicCartAdd() {
    const state = this.#getState();
    const { activeSurcharges } = this.#calcTotal(state);
    const variantId = this.#form?.querySelector('input[name="id"]')?.value;
    if (!variantId) return;

    // Generate a fresh configuration ID for every add-to-cart (prevents merging).
    this.#configurationId = globalThis.crypto?.randomUUID?.() || `jv-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const buildId = this.#configurationId;

    // Human-readable label maps for machine values. BUCKLE_LABELS is module-scoped
    // so the build summary and the cart payload cannot drift apart.
    const SPRING_LABELS = { QUICK_RELEASE: 'Quick-release' };

    // Build properties — human-readable values for customer-facing display.
    const parentProps = {
      // Private routing properties
      '_configuration_id': buildId,
      '_role': 'parent',
      '_estimated_total': state.config_total_snapshot,
      // Public build properties
      'Color': state.color,
      'Lug Width (mm)': state.lug_width_mm,
      'Buckle Width (mm)': state.buckle_width_mm,
      'Length': state.length_label,
      'Watch End': (END_LABELS[state.watch_end_shape] || state.watch_end_shape) + (this.#isDefault('watch_end', state.watch_end_shape) ? ' — as pictured' : ''),
      'Style': (STYLE_LABELS[state.style] || state.style) + (this.#isDefault('style', state.style) ? ' — as pictured' : ''),
      'Buckle': this.#isDefault('buckle', state.buckle_type) && state.buckle_finish === 'AS_PICTURED'
        ? `${BUCKLE_LABELS[state.buckle_type] || state.buckle_type} — as pictured`
        : `${BUCKLE_LABELS[state.buckle_type] || state.buckle_type} ${FINISH_LABELS[state.buckle_finish] || state.buckle_finish}`.trim(),
      'Spring Bars': SPRING_LABELS[state.spring_bar_type] || state.spring_bar_type,
      'Stitch Colour': STITCH_LABELS[state.stitch_color] || state.stitch_color,
    };
    // Conditional properties — only include when active (suppress false/No).
    if (state.length_code === 'CUSTOM') {
      parentProps['Custom Long Side (mm)'] = state.custom_long_side_mm;
      parentProps['Custom Short Side (mm)'] = state.custom_short_side_mm;
    }
    if (state.engraving === 'true' && state.engraving_text) {
      parentProps['Engraving'] = state.engraving_text;
    }
    if (state.watch_help === 'true') {
      if (state.watch_brand) parentProps['Watch Brand'] = state.watch_brand;
      if (state.watch_model) parentProps['Watch Model / Notes'] = state.watch_model;
      parentProps['Fit Review'] = 'Requested';
    }

    // Remove blank/empty/false properties (Shopify shows them otherwise)
    for (const [key, value] of Object.entries(parentProps)) {
      if (value === '' || value === undefined || value === null || value === 'false') delete parentProps[key];
    }

    // Collect the optional watch photo file.
    const photoInput = this.querySelector('[data-watch-photo]');
    const photoFile = photoInput instanceof HTMLInputElement ? photoInput.files?.[0] : null;
    const hasPhoto = photoFile && /^image\/(jpeg|png|webp)$/.test(photoFile.type) && photoFile.size <= 8 * 1024 * 1024;

    const addonItems = [];
    const baseSku = this.dataset.baseSku || '';
    for (const surcharge of activeSurcharges) {
      const addon = this.#addonMap[surcharge.sku];
      if (!addon?.id) continue;
      addonItems.push({
        id: addon.id,
        quantity: 1,
        properties: {
          '_configuration_id': buildId,
          '_role': 'addon',
          '_addon_type': surcharge.key,
          '_addon_label': surcharge.label,
          '_parent_sku': baseSku,
        },
      });
    }

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach(c => {
      if (c.dataset.sectionId) sectionIds.push(c.dataset.sectionId);
    });

    this.#setBusyState(true);
    // Say so on the button the moment the tap lands. The request behind this
    // can run for seconds on a photo add, and until now nothing on screen
    // acknowledged the tap at all.
    this.#setAtcBusyLabel(hasPhoto ? 'Uploading photo…' : 'Adding…');
    const productForm = this.#form?.closest('product-form-component');

    let resolveEvent, rejectEvent;
    const eventPromise = new Promise((res, rej) => { resolveEvent = res; rejectEvent = rej; });

    try {
      const evt = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
      evt.action = 'add';
      evt.context = 'product';
      const allLines = [{ merchandiseId: variantId, quantity: 1 }, ...addonItems.map(i => ({ merchandiseId: String(i.id), quantity: i.quantity }))];
      evt.lines = allLines;
      evt.promise = eventPromise;
      (productForm || this).dispatchEvent(evt);
    } catch { /* event dispatch is best-effort */ }

    // Airplane mode switched on mid-upload leaves the request hanging: iOS holds
    // the socket until TCP gives up, tens of seconds later, so nothing rejects
    // and the drawer sits on "Adding your strap…" forever. One budget covers
    // the whole submit, both requests of the photo path included.
    const abortController = new AbortController();
    const abortTimer = setTimeout(() => abortController.abort(), 25000);

    try {
      let lastData;

      if (hasPhoto) {
        const resized = await this.#preparedPhoto(photoFile);
        const fd = new FormData();
        fd.append('id', variantId);
        fd.append('quantity', '1');
        for (const [k, v] of Object.entries(parentProps)) fd.append(`properties[${k}]`, v);
        // The re-encoded file carries its own .jpg name; the property key is
        // untouched, so Shopify still returns the /uploads/ URL the workshop
        // export and the order email read.
        fd.append('properties[_watch_photo]', resized, resized.name || photoFile.name);
        if (!addonItems.length && sectionIds.length) fd.append('sections', sectionIds.join(','));
        const parentResp = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/json' },
          body: fd,
          signal: abortController.signal,
        });
        const parentData = await parentResp.json();
        if (!parentResp.ok || parentData.status) {
          console.error('[jv-cfg] Parent cart add failed:', parentData);
          const status = this.querySelector('[data-sr-status]');
          if (status) status.textContent = parentData.description || parentData.message || 'Could not add to cart. Please try again.';
          rejectEvent?.(new Error(parentData.description || 'Cart add failed'));
          return;
        }
        if (addonItems.length) {
          const addonResp = await fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ items: addonItems, sections: sectionIds.join(',') }),
            signal: abortController.signal,
          });
          lastData = await addonResp.json();
        } else {
          lastData = parentData;
        }
      } else {
        const items = [{ id: parseInt(variantId, 10), quantity: 1, properties: parentProps }, ...addonItems];
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds.join(',') }),
          signal: abortController.signal,
        });
        lastData = await response.json();
        if (!response.ok || lastData.status) {
          console.error('[jv-cfg] Cart add failed:', lastData);
          const status = this.querySelector('[data-sr-status]');
          if (status) status.textContent = lastData.description || lastData.message || 'Could not add to cart. Please try again.';
          rejectEvent?.(new Error(lastData.description || 'Cart add failed'));
          return;
        }
      }

      resolveEvent?.({
        detail: {
          sections: lastData?.sections || {},
          source: 'jv-configurator',
          didError: false,
        },
      });

      this.#configurationId = globalThis.crypto?.randomUUID?.() || `jv-${Date.now()}-${Math.random().toString(36).slice(2)}`;

    } catch (err) {
      console.error('[jv-cfg] Cart add error:', err);
      // A thrown fetch — the connection dropping mid-upload — used to leave
      // nothing on screen: the rejected-response paths above write to the
      // status line, this one did not. It matters more now that the drawer
      // opens on the tap and closes again when an add against an empty cart
      // fails, because the message has to survive here, next to the button.
      const status = this.querySelector('[data-sr-status]');
      if (status) status.textContent = 'Could not add to cart. Please check your connection and try again.';
      rejectEvent?.(err);
    } finally {
      clearTimeout(abortTimer);
      this.#setAtcBusyLabel(null);
      this.#setBusyState(false);
    }
  }

  /**
   * Shrinks a watch photo to something that uploads in a reasonable time.
   *
   * Always re-encodes: the previous version returned anything already under
   * 2000px untouched, which let full-size phone photos through, and it kept PNG
   * input as PNG, where `quality` does nothing and a photo stays enormous.
   *
   * Every failure path returns the original file. A slow add is a bad add; a
   * broken one is worse.
   */
  async #resizePhoto(file) {
    const MAX_EDGE = 1200;
    const QUALITY = 0.75;

    try {
      // `from-image` bakes the EXIF rotation into the pixels. It matters now in
      // a way it did not before: small photos used to be forwarded untouched
      // and the viewer applied the tag, but everything goes through the canvas
      // from here, and a canvas draws the raw buffer. Without this an iPhone
      // portrait reaches the workshop on its side.
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });

      const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));
      const w = Math.round(bitmap.width * scale);
      const h = Math.round(bitmap.height * scale);

      const canvas = new OffscreenCanvas(w, h);
      const ctx = canvas.getContext('2d');
      ctx.drawImage(bitmap, 0, 0, w, h);
      bitmap.close();

      const blob = await canvas.convertToBlob({ type: 'image/jpeg', quality: QUALITY });

      // Safari hands back PNG when it cannot encode what was asked for, so
      // trust the blob and not the argument. And if the re-encode came out
      // larger — a small or already-optimised file — the original is better.
      if (blob.type !== 'image/jpeg' || blob.size >= file.size) {
        return file;
      }

      const name = `${file.name.replace(/\.[^.]+$/, '')}.jpg`;
      return new File([blob], name, { type: 'image/jpeg' });
    } catch (error) {
      console.warn('[jv-cfg] Photo re-encode failed, sending the original:', error);
      return file;
    }
  }


  #getState() {
    const lengthSelect = this.querySelector('[data-cfg-field="length_code"]');
    const selectedLength = lengthSelect instanceof HTMLSelectElement ? lengthSelect.selectedOptions[0] : null;
    const style = this.#radioVal('style') || 'SLIM';
    const buckleType = this.#radioVal('buckle_family') || 'CLASSIC';
    const rawFinish = this.#radioVal('buckle_finish');
    const buckleFinish = rawFinish || (this.#defaults.buckle === buckleType ? 'AS_PICTURED' : 'SILVER');
    const stitchMode = style === 'STITCHLESS' ? 'NO_STITCH' : (this.#radioVal('stitch_color_mode') || 'AS_PICTURED');
    const state = {
      color: this.dataset.colorLabel || '',
      lug_width_mm: this.#selectVal('lug_width_mm'),
      buckle_width_mm: this.#selectVal('buckle_width_mm'),
      length_code: this.#selectVal('length_code'),
      length_label: this.#selectVal('length_code') ? (selectedLength?.textContent?.trim() || '') : '',
      watch_end_shape: this.#radioVal('watch_end_shape') || 'STRAIGHT',
      style,
      buckle_type: buckleType,
      buckle_finish: buckleFinish,
      spring_bar_type: 'QUICK_RELEASE',
      stitch_color: stitchMode === 'CUSTOM' ? this.#radioVal('stitch_color_custom') : stitchMode,
      engraving: this.#checked('engraving') ? 'true' : 'false',
      watch_brand: this.#inputVal('watch_brand'),
      watch_model: this.#inputVal('watch_model'),
      watch_help: this.#checked('watch_help') ? 'true' : 'false',
      configuration_id: this.#configurationId,
      role: 'parent',
    };
    if (state.length_code === 'CUSTOM') {
      state.custom_long_side_mm = this.#inputVal('custom_long_side_mm');
      state.custom_short_side_mm = this.#inputVal('custom_short_side_mm');
      state.length_label = `Custom — ${state.custom_long_side_mm || '?'} / ${state.custom_short_side_mm || '?'} mm`;
    }
    if (state.engraving === 'true') state.engraving_text = this.#inputVal('engraving_text');

    const { total } = this.#calcTotal(state);
    state.config_total_snapshot = total.toFixed(2);
    return state;
  }

  #calcTotal(state) {
    const activeSurcharges = [];
    // The amount comes from the variant, same as the price beside each option.
    // It used to come from a constant here while the option labels used the
    // variant, so changing a price in the admin quietly desynced the estimated
    // total from what the customer was actually charged.
    const add = (key) => activeSurcharges.push({ key, ...SURCHARGES[key], amount: this.#surchargePrice(key) });

    if (state.style === 'SIGNATURE' && !this.#isIncluded('style', 'SIGNATURE')) add('SIGNATURE');
    if (state.watch_end_shape === 'CURVED' && !this.#isIncluded('watch_end', 'CURVED')) add('CURVED');
    if (state.engraving === 'true') add('ENGRAVING');
    if (state.buckle_type === 'PRE_V' && !this.#isIncluded('buckle', 'PRE_V')) add('PRE_V');
    if (state.buckle_type === 'BUTTERFLY' && !this.#isIncluded('buckle', 'BUTTERFLY')) add('BUTTERFLY');
    if (state.buckle_type === 'HIDDEN_DEPLOYANT' && !this.#isIncluded('buckle', 'HIDDEN_DEPLOYANT')) add('HIDDEN_DEPLOYANT');

    const unresolved = activeSurcharges.filter(item => {
      const addon = this.#addonMap[item.sku];
      return !addon?.id || !addon.available;
    });
    return {
      total: this.#basePrice + activeSurcharges.reduce((sum, item) => sum + item.amount, 0),
      activeSurcharges,
      unresolved,
    };
  }

  #syncHiddenProps() {
    const state = this.#getState();
    for (const input of this.querySelectorAll('.jv-cfg__hidden-props input[data-prop]')) {
      const value = state[input.dataset.prop];
      if (value === undefined || value === null || value === '') {
        input.disabled = true;
        input.value = '';
      } else {
        input.disabled = false;
        input.value = String(value);
      }
    }
  }

  #updateSummary() {
    const state = this.#getState();
    const { total, activeSurcharges, unresolved } = this.#calcTotal(state);
    // Every match, not the first. While the owner moves the summary into an
    // accordion row the old block and the new row both exist, and filling only
    // the first would leave whichever came second looking broken — which is the
    // one being checked.
    const summaries = this.#allInSection('[data-summary-list]');
    if (summaries.length) {
      let html = '';
      html += this.#summaryRow('Color', this.#escapeHtml(state.color));
      // The three required measurements keep their row whether or not they have
      // been answered. Dropping them made the summary shorter and told the
      // customer nothing about what was still holding up the add; a dash says
      // the question exists and is open.
      const pending = '<span class="jv-cfg__summary-pending">—</span>';
      html += this.#summaryRow('Lug width', state.lug_width_mm ? `${state.lug_width_mm} mm` : pending);
      html += this.#summaryRow('Buckle width', state.buckle_width_mm ? `${state.buckle_width_mm} mm` : pending);
      html += this.#summaryRow('Length', state.length_label ? this.#escapeHtml(state.length_label) : pending);
      html += this.#summaryRow('Watch end', this.#summaryOptionValue('watch_end', state.watch_end_shape, state.watch_end_shape === 'CURVED' ? 'Curved' : 'Straight'));
      html += this.#summaryRow('Style', this.#summaryOptionValue('style', state.style, STYLE_LABELS[state.style] || state.style));
      const buckleName = BUCKLE_LABELS[state.buckle_type] || state.buckle_type;
      if (this.#isDefault('buckle', state.buckle_type) && state.buckle_finish === 'AS_PICTURED') {
        html += this.#summaryRow('Buckle', `${buckleName} (as pictured)`);
      } else {
        const finishLabel = FINISH_LABELS[state.buckle_finish] || state.buckle_finish;
        const bucklePrice = this.#getAxisOptionPrice('buckle', state.buckle_type);
        const buckleSurcharge = (!this.#isIncluded('buckle', state.buckle_type) && bucklePrice > 0) ? ` <span class="jv-cfg__surcharge">${this.#formatSurcharge(bucklePrice)}</span>` : '';
        html += this.#summaryRow('Buckle', `${buckleName} ${finishLabel}${buckleSurcharge}`);
      }
      html += this.#summaryRow('Stitch', STITCH_LABELS[state.stitch_color] || state.stitch_color || 'Select a colour');
      html += this.#summaryRow('Spring bars', 'Quick-release (included)');
      if (state.engraving === 'true') html += this.#summaryRow('Engraving', `"${this.#escapeHtml(state.engraving_text || '')}" <span class="jv-cfg__surcharge">${this.#formatSurcharge(this.#surchargePrice('ENGRAVING'))}</span>`);
      if (state.watch_help === 'true') html += this.#summaryRow('Fit review', this.#escapeHtml([state.watch_brand, state.watch_model].filter(Boolean).join(' · ') || 'Watch photo attached'));
      for (const list of summaries) list.innerHTML = html;
    }

    this.#renderRowValues(state);
    // The total shows twice now — above add to cart and in the build summary's
    // own header, which sits outside this element — so the search starts at the
    // section rather than here.
    this.#allInSection('[data-total-display]').forEach(element => { element.textContent = this.#formatMoney(total); });
    this.#renderDelivery(total);

    const missing = this.#getMissingFields(state);
    const complete = missing.length === 0;
    const hasUnresolved = unresolved.length > 0;

    this.querySelector('[data-addon-warning]')?.classList.toggle('hidden', !hasUnresolved);
    // Missing fields no longer grey the button out. A disabled button cannot be
    // clicked, so the validation below it — the red borders, the message under
    // each field, the scroll — was unreachable: the customer saw a dead control
    // and no reason for it. The button stays live and #handleProductFormSubmit
    // is what refuses the add.
    //
    // An unavailable add-on is different in kind. Nothing the customer types
    // fixes it, so the button does stay disabled there, with [data-addon-warning]
    // saying why.
    this.#setBusyState(hasUnresolved);
    this.#setAtcIdleLabel();
    // After #setAtcIdleLabel and #setBusyState, both of which it mirrors.
    this.#renderStickyBar(total);

    const status = this.querySelector('[data-sr-status]');
    if (status) {
      if (!complete) status.textContent = `Complete all required fields: ${missing.map(f => f.label).join(', ')}. Estimated total ${this.#formatMoney(total)}.`;
      else if (hasUnresolved) status.textContent = `Estimated total ${this.#formatMoney(total)}. Some add-on variants are unavailable.`;
      else status.textContent = `Configuration complete. Estimated total ${this.#formatMoney(total)}.`;
    }
  }

  #isComplete(state) {
    const requiredControls = this.querySelectorAll('select[required], input[required]');
    if ([...requiredControls].some(control => !control.checkValidity())) return false;
    if (!state.lug_width_mm || !state.buckle_width_mm || !state.length_code || !state.buckle_type) return false;
    if (!this.#isBuckleAllowed(state.lug_width_mm, state.buckle_width_mm)) return false;
    if (!state.buckle_finish && this.#defaults.buckle !== state.buckle_type) return false;
    if (state.length_code === 'CUSTOM' && (!state.custom_long_side_mm || !state.custom_short_side_mm)) return false;
    if (!state.stitch_color) return false;
    if (state.engraving === 'true' && !state.engraving_text) return false;

    const currentVariant = this.#form?.querySelector('input[name="id"]')?.value || '';
    if (this.#initialVariantId && currentVariant && currentVariant !== this.#initialVariantId) return false;
    return true;
  }

  #shouldBlockCheckout() {
    const state = this.#getState();
    const { unresolved } = this.#calcTotal(state);
    return !this.#isComplete(state) || unresolved.length > 0;
  }

  /**
   * The photo being prepared, keyed by the exact File it came from, so a submit
   * that lands mid-encode awaits the same work instead of starting it again.
   * @type {{ file: File, promise: Promise<File> } | null}
   */
  #photoPrepared = null;

  /** Awaits the encode started at selection time, or does it now if it wasn't. */
  async #preparedPhoto(file) {
    if (this.#photoPrepared?.file === file) return this.#photoPrepared.promise;
    return this.#resizePhoto(file);
  }

  /**
   * Swaps the add-to-cart label while a submit is in flight.
   *
   * Separate from #setBusyState, which owns the disabled attribute: the label
   * says what is happening, the attribute stops a second submit.
   *
   * @param {string | null} label - Busy text, or null to restore.
   */
  /** Remembers the theme's own add-to-cart wording, to restore and to fall back on. */
  #captureAtcLabel() {
    const target = this.#atcTextTarget();
    if (target && this.#atcDefaultLabel === null) this.#atcDefaultLabel = target.textContent?.trim() || 'Add to cart';
  }

  #atcTextTarget() {
    const form = document.getElementById(this.#formId);
    const button = form?.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return null;
    return button.querySelector('.button-text') ?? button;
  }

  /**
   * Says what the button is waiting for while the build is unfinished.
   *
   * Lug width is the one choice with nothing sensible to default to — the
   * buckle follows from it and the length has its own picker — so it is the
   * only one worth naming on the button. Skipped while a submit is in flight,
   * where #setAtcBusyLabel owns the text.
   */
  #setAtcIdleLabel() {
    const target = this.#atcTextTarget();
    if (!target) return;
    if (target.closest('button')?.dataset.jvAtcLabel !== undefined) return;
    target.textContent = this.#selectVal('lug_width_mm') ? this.#atcDefaultLabel ?? 'Add to cart' : 'Select lug width';
  }

  #setAtcBusyLabel(label) {
    const form = document.getElementById(this.#formId);
    const button = form?.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;

    const target = button.querySelector('.button-text') ?? button;

    if (label) {
      if (button.dataset.jvAtcLabel === undefined) button.dataset.jvAtcLabel = target.textContent ?? '';
      target.textContent = label;
      button.setAttribute('aria-busy', 'true');
      return;
    }

    if (button.dataset.jvAtcLabel !== undefined) {
      target.textContent = button.dataset.jvAtcLabel;
      delete button.dataset.jvAtcLabel;
    }
    button.removeAttribute('aria-busy');
  }

  #setBusyState(disabled) {
    const form = document.getElementById(this.#formId);
    const button = form?.closest('product-form-component')?.querySelector('[ref="addToCartButton"]');
    if (!(button instanceof HTMLButtonElement)) return;
    if (disabled) {
      button.disabled = true;
      button.dataset.configuratorBlocked = 'true';
    } else if (button.dataset.configuratorBlocked === 'true') {
      button.disabled = false;
      delete button.dataset.configuratorBlocked;
    }
  }

  /** Returns an ordered list of missing/invalid fields with human-readable labels. */
  #getMissingFields(state) {
    if (!state) state = this.#getState();
    const missing = [];
    // Check fields in DOM order (top → bottom).
    const checks = [
      { field: 'lug_width_mm', value: state.lug_width_mm },
      // A width is not enough: it has to be even, inside the buckle family's
      // range and no wider than the lug. The select offers nothing else, so
      // this catches a value the browser restored or a page loaded before the
      // rule changed.
      { field: 'buckle_width_mm', value: state.buckle_width_mm, invalid: Boolean(state.buckle_width_mm) && !this.#isBuckleAllowed(state.lug_width_mm, state.buckle_width_mm) },
      { field: 'length_code', value: state.length_code },
      { field: 'custom_long_side_mm', value: state.custom_long_side_mm, condition: state.length_code === 'CUSTOM' },
      { field: 'custom_short_side_mm', value: state.custom_short_side_mm, condition: state.length_code === 'CUSTOM' },
      { field: 'stitch_color', value: state.stitch_color, selector: 'input[data-cfg-field="stitch_color_mode"], input[data-cfg-field="stitch_color_custom"]' },
      { field: 'engraving_text', value: state.engraving_text, condition: state.engraving === 'true' },
      { field: 'watch_brand', value: state.watch_brand, condition: state.watch_help === 'true' },
      { field: 'watch_model', value: state.watch_model, condition: state.watch_help === 'true' },
    ];
    for (const check of checks) {
      if (check.condition === false) continue;
      if (check.value && !check.invalid) continue;
      const label = this.#getFieldLabel(check.field, check.selector);
      // The selector travels with the entry: stitch_color has no control of its
      // own, so whoever marks or clears it needs to know what to look for.
      if (label) missing.push({ field: check.field, label, selector: check.selector, message: check.invalid ? FIELD_ERRORS[`${check.field}_invalid`] : undefined });
    }
    return missing;
  }

  /** Derives a human-readable label from the DOM <label> associated with a field. */
  #getFieldLabel(fieldName, selector) {
    const sel = selector || `[data-cfg-field="${fieldName}"]`;
    const el = this.querySelector(sel);
    if (!el) return fieldName;
    // Find the associated <label> via for attribute or ancestor.
    const id = el.id;
    let labelEl = id ? this.querySelector(`label[for="${CSS.escape(id)}"]`) : null;
    if (!labelEl) labelEl = el.closest('.jv-cfg__field')?.querySelector('.jv-cfg__label');
    if (!labelEl) {
      // Radio groups: look for parent fieldset's legend.
      const legend = el.closest('fieldset')?.querySelector('legend');
      if (legend) return legend.textContent.replace(/\*/g, '').trim();
      return fieldName;
    }
    // Strip the * abbr from label text.
    return labelEl.textContent.replace(/\*/g, '').trim();
  }

  /** Show field-level validation errors + scroll to first missing field. */
  #showValidation() {
    const state = this.#getState();
    const missing = this.#getMissingFields(state);
    if (missing.length === 0) return;

    for (const { field, selector, message } of missing) {
      this.#setFieldError(field, selector, message);
    }

    const first = missing[0];
    const el = this.querySelector(first.selector || `[data-cfg-field="${first.field}"]`);
    if (!el) return;

    // The buckle width select lives behind "Other width". A hidden element has
    // no offset parent, so scrolling to it would land at the top of the page
    // and focus would go nowhere: reveal it first, then scroll.
    if (el.closest('[data-taper-other].hidden')) {
      this.#toggleTaperOther(true);
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      });
      return;
    }

    const dialog = el.closest('dialog[data-cfg-dialog]');
    if (dialog && !dialog.open) {
      dialog.showModal();
      requestAnimationFrame(() => {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      });
      return;
    }

    this.#scrollToElement(el);
  }

  /** Scroll an element into view with sticky header offset. */
  #scrollToElement(el) {
    const headerHeight = parseInt(getComputedStyle(document.body).getPropertyValue('--header-height') || '60', 10);
    // Use absolute position: walk offsetParent chain for reliable Y.
    let top = 0;
    let node = el;
    while (node) {
      top += node.offsetTop;
      node = node.offsetParent;
    }
    window.scrollTo({ top: Math.max(0, top - headerHeight - 20), behavior: 'smooth' });
    setTimeout(() => {
      const focusTarget = el instanceof HTMLSelectElement || el instanceof HTMLInputElement ? el : el.querySelector('input, select');
      focusTarget?.focus({ preventScroll: true });
    }, 400);
  }

  /** Mark a single field as having an error. */
  #setFieldError(fieldName, selector, message) {
    // stitch_color is not a control — it is whichever of stitch_color_mode or
    // stitch_color_custom is in play, which is why #getMissingFields carries a
    // selector for it. Without honouring that here the lookup found nothing and
    // returned, so the one field that could be missing inside the modal was
    // never marked at all.
    const el = this.querySelector(selector || `[data-cfg-field="${fieldName}"]`);
    if (!el) return;
    const wrapper = el.closest('.jv-cfg__field') || el.closest('fieldset');
    if (!wrapper) return;

    // Set aria-invalid.
    el.setAttribute('aria-invalid', 'true');

    // Add error class to wrapper.
    wrapper.classList.add('jv-cfg__field--error');

    // Insert error message if not already present.
    const errId = `jv-err-${fieldName}`;
    if (!wrapper.querySelector(`#${CSS.escape(errId)}`)) {
      const msg = document.createElement('p');
      msg.id = errId;
      msg.className = 'jv-cfg__field-error';
      msg.setAttribute('role', 'alert');
      msg.textContent = message || FIELD_ERRORS[fieldName] || `Please select your ${this.#getFieldLabel(fieldName).toLowerCase()}`;
      el.setAttribute('aria-describedby', errId);
      // Insert after the control or after its parent label-row.
      const insertAfter = el.closest('.jv-cfg__label-row')?.nextElementSibling || el;
      insertAfter.after(msg);
    }
  }

  /** Clear error state from a single field. */
  #clearFieldError(fieldName, selector) {
    const el = this.querySelector(selector || `[data-cfg-field="${fieldName}"]`);
    if (!el) return;
    el.removeAttribute('aria-invalid');
    el.removeAttribute('aria-describedby');
    const wrapper = el.closest('.jv-cfg__field') || el.closest('fieldset');
    if (!wrapper) return;
    wrapper.classList.remove('jv-cfg__field--error');
    // Clear by wrapper rather than by id. The error under the stitch swatches
    // is keyed stitch_color while the control that changes is
    // stitch_color_mode, so matching on the name left it on screen after the
    // customer had answered it.
    for (const msg of wrapper.querySelectorAll('.jv-cfg__field-error')) msg.remove();
  }

  /** Clear all field errors. */
  #clearAllFieldErrors() {
    for (const errEl of this.querySelectorAll('.jv-cfg__field-error')) errEl.remove();
    for (const el of this.querySelectorAll('[aria-invalid]')) {
      el.removeAttribute('aria-invalid');
      el.removeAttribute('aria-describedby');
    }
    for (const el of this.querySelectorAll('.jv-cfg__field--error')) {
      el.classList.remove('jv-cfg__field--error');
    }
  }

  #parseDefaults() {
    const ds = this.dataset;
    this.#defaults.style = DEFAULT_META_MAP.style[ds.defaultStyle?.toLowerCase()] || null;
    this.#defaults.buckle = DEFAULT_META_MAP.buckle[ds.defaultBuckle?.toLowerCase()] || null;
    this.#defaults.watch_end = DEFAULT_META_MAP.watch_end[ds.defaultWatchEnd?.toLowerCase()] || null;
    if (!this.#defaults.style && !this.#defaults.buckle && !this.#defaults.watch_end) return;

    if (this.#defaults.watch_end) {
      const r = this.querySelector(`input[data-cfg-field="watch_end_shape"][value="${this.#defaults.watch_end}"]`);
      if (r) r.checked = true;
    }
    if (this.#defaults.style) {
      const r = this.querySelector(`input[data-cfg-field="style"][value="${this.#defaults.style}"]`);
      if (r) r.checked = true;
    }
    if (this.#defaults.buckle) {
      const r = this.querySelector(`input[data-cfg-field="buckle_family"][value="${this.#defaults.buckle}"]`);
      if (r) r.checked = true;
    }
    this.#syncDefaultBuckleFinish();
    this.#updateOptionLabels();
  }

  #syncDefaultBuckleFinish() {
    const family = this.#radioVal('buckle_family') || 'CLASSIC';
    for (const panel of this.querySelectorAll('[data-buckle-panel]')) {
      const isDefaultPanel = this.#defaults.buckle && panel.dataset.bucklePanel === this.#defaults.buckle;
      const isActive = panel.dataset.bucklePanel === family;
      const legend = panel.querySelector('.jv-cfg__microcopy');
      const finishes = panel.querySelectorAll('input[data-cfg-field="buckle_finish"]');
      if (isDefaultPanel && isActive) {
        finishes.forEach(r => { r.checked = false; r.removeAttribute('required'); });
        if (legend) {
          if (!legend.dataset.originalText) legend.dataset.originalText = legend.textContent;
          legend.textContent = 'Optional — leave unselected to match the photo.';
        }
      } else if (isActive) {
        finishes.forEach(r => r.setAttribute('required', ''));
        if (legend?.dataset.originalText) legend.textContent = legend.dataset.originalText;
      }
    }
  }

  #updateOptionLabels() {
    const axes = [
      { axis: 'watch_end', field: 'watch_end_shape', prefix: '' },
      { axis: 'style', field: 'style', prefix: '' },
      { axis: 'buckle', field: 'buckle_family', prefix: '' },
    ];
    for (const { axis, field, prefix } of axes) {
      // The buckle axis is relabelled on every strap, declared default or
      // not: with none declared the strap is read as a Classic one, which is
      // the assumption the block already renders, so the tiles say the same
      // thing before and after the script runs. The other two axes have
      // nothing to say without a default and are left as the block wrote them.
      if (axis !== 'buckle' && !this.#defaults[axis]) continue;
      for (const radio of this.querySelectorAll(`input[data-cfg-field="${field}"]`)) {
        const small = radio.closest('label')?.querySelector('small');
        if (!small) continue;
        // The card's own description — "Padded", "Unpadded" — comes from the
        // block, so there is one copy of it. Rewriting the label used to drop
        // it: Signature went from "Padded · +$3" to a bare "+$3" the moment the
        // script ran on a product that declares a default style.
        const parts = axis === 'buckle'
          ? this.#buckleTileLabel(radio.value)
          : this.#getOptionLabel(axis, radio.value, small.dataset.desc || '');
        if (!parts) continue;

        // Rebuilt as nodes, not as a string. Writing textContent here wiped the
        // span the block renders around the price, so any card this touched —
        // every buckle on a product that declares a default — lost the emphasis
        // and the price went back to grey small print.
        small.replaceChildren();
        const muted = prefix + parts.muted;
        if (muted) small.append(muted);
        if (parts.price) {
          const priceEl = document.createElement('span');
          priceEl.className = 'jv-cfg__price';
          priceEl.textContent = parts.price;
          small.append(priceEl);
        }
      }
    }
  }

  /**
   * Splits an option's label into the part that stays muted and the price.
   *
   * Two pieces rather than one string so the caller can give the price its own
   * element: a description like "Unpadded" reads quietly, a surcharge is a
   * figure the customer is agreeing to pay.
   *
   * @returns {{muted: string, price: string | null} | null}
   */
  #getOptionLabel(axis, key, desc = '') {
    const defaultKey = this.#defaults[axis];
    if (!defaultKey) return null;

    // "Unpadded · As pictured · Included" says the same thing twice, so a card
    // that carries its own description drops the "As pictured" half.
    const lead = desc ? `${desc} · ` : '';

    // "Included" is on the group's value line, once, so a tile that costs
    // nothing carries only its name and its own description.
    if (key === defaultKey) return { muted: desc, price: null };

    const selectedPrice = this.#getAxisOptionPrice(axis, key);
    const defaultPrice = this.#getAxisOptionPrice(axis, defaultKey);
    if (selectedPrice <= defaultPrice) return { muted: desc, price: null };
    return { muted: lead, price: this.#formatSurcharge(selectedPrice) };
  }

  /** The add-on variant's price, in dollars. Zero when the variant is missing. */
  #surchargePrice(surchargeKey) {
    const sku = SURCHARGES[surchargeKey]?.sku;
    const addon = sku ? this.#addonMap[sku] : null;
    return addon?.price_cents != null ? addon.price_cents / 100 : 0;
  }

  /** "+$12" for a round figure, "+$4.95" otherwise — matching the Liquid labels. */
  #formatSurcharge(amount) {
    return `+$${Number.isInteger(amount) ? amount : amount.toFixed(2)}`;
  }

  #getAxisOptionPrice(axis, key) {
    const surchargeKey = AXIS_SURCHARGE_KEYS[axis]?.[key];
    return surchargeKey ? this.#surchargePrice(surchargeKey) : 0;
  }

  #isIncluded(axis, key) {
    const defaultKey = this.#defaults[axis];
    if (!defaultKey) return false;
    return this.#getAxisOptionPrice(axis, key) <= this.#getAxisOptionPrice(axis, defaultKey);
  }

  #isDefault(axis, key) { return this.#defaults[axis] === key; }

  /**
   * The buckle a strap is photographed with — Classic when the product does
   * not say. Classic is the only family with no add-on variant behind it, so
   * the assumption agrees with #isIncluded on every strap: a Pre-V there
   * still reads as a surcharge, and still is one. Display only; #isDefault
   * and #isIncluded are untouched, because they also decide what goes in the
   * cart and what the line-item property says.
   */
  #effectiveBuckleDefault() { return this.#defaults.buckle || 'CLASSIC'; }

  #buckleLabelDefault() { return this.dataset.buckleLabelDefault || 'As pictured'; }

  #buckleLabelFree() { return this.dataset.buckleLabelFree || 'No extra cost'; }

  /**
   * The one value under a buckle tile's name. "As pictured" for the buckle in
   * the photographs, "No extra cost" for another the price already covers,
   * and otherwise the figure the cart will charge.
   *
   * The comparison is #isIncluded's, against #effectiveBuckleDefault rather
   * than #defaults.buckle, so a strap with no metafield shows "As pictured"
   * under Classic instead of the word for owing nothing.
   *
   * @returns {{muted: string, price: string | null}}
   */
  #buckleTileLabel(key) {
    const defaultKey = this.#effectiveBuckleDefault();
    if (key === defaultKey) return { muted: this.#buckleLabelDefault(), price: null };

    const price = this.#getAxisOptionPrice('buckle', key);
    if (price <= this.#getAxisOptionPrice('buckle', defaultKey)) {
      return { muted: this.#buckleLabelFree(), price: null };
    }
    return { muted: '', price: this.#formatSurcharge(price) };
  }

  #summaryOptionValue(axis, key, label) {
    if (this.#isDefault(axis, key)) return `${label} (as pictured)`;
    if (!this.#isIncluded(axis, key)) {
      const price = this.#getAxisOptionPrice(axis, key);
      if (price > 0) return `${label} <span class="jv-cfg__surcharge">${this.#formatSurcharge(price)}</span>`;
    }
    return label;
  }

  #selectVal(name) {
    const element = this.querySelector(`select[data-cfg-field="${name}"]`);
    return element instanceof HTMLSelectElement ? element.value : '';
  }

  #inputVal(name) {
    const element = this.querySelector(`[data-cfg-field="${name}"]`);
    return element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement ? element.value.trim() : '';
  }

  #radioVal(name) {
    const element = this.querySelector(`input[type="radio"][data-cfg-field="${name}"]:checked:not(:disabled)`);
    return element instanceof HTMLInputElement ? element.value : '';
  }

  #checked(name) {
    const element = this.querySelector(`input[type="checkbox"][data-cfg-field="${name}"]`);
    return element instanceof HTMLInputElement && element.checked;
  }

  #summaryRow(label, value) { return `<dt>${this.#escapeHtml(label)}</dt><dd>${value}</dd>`; }
  /** "$89.00", or "$89.00 USD" when Theme settings → Currency format shows codes on product pages. */
  #formatMoney(amount) {
    const money = `$${amount.toFixed(2)}`;
    return this.dataset.showCurrencyCode === 'true' ? `${money} ${this.dataset.currency || 'USD'}` : money;
  }
  #escapeHtml(value) { const div = document.createElement('div'); div.textContent = String(value); return div.innerHTML; }
}

if (!customElements.get('leather-strap-configurator')) customElements.define('leather-strap-configurator', LeatherStrapConfigurator);
